import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useAuthStore } from '@/stores/auth.store';
import { formatCountdown, resolveSessionState, type SessionState } from '@/utils/session-expiry';

/** Events that count as the user still being at the keyboard. `scroll` is included
 *  because reading a long contact list is genuine use. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

/** Activity is recorded at most this often — a two-minute idle window does not need
 *  millisecond resolution, and this keeps the listeners off the hot path. */
const ACTIVITY_THROTTLE_MS = 5_000;

/** How long to wait after a failed silent renewal before trying again, so a dropped
 *  connection does not produce one Cognito call per second. */
const RENEW_BACKOFF_MS = 30_000;

const TICK_MS = 1_000;

export function useSessionTimer() {
  const auth = useAuthStore();

  const now = ref(Date.now());
  const lastActivityAt = ref(Date.now());
  const isExtending = ref(false);
  const renewBlockedUntil = ref(0);

  let intervalId: ReturnType<typeof setInterval> | undefined;

  const state = computed<SessionState>(() =>
    resolveSessionState({
      now: now.value,
      expiresAt: auth.expiresAt,
      sessionStartedAt: auth.sessionStartedAt,
      lastActivityAt: lastActivityAt.value,
    }),
  );

  const countdown = computed(() => formatCountdown(state.value.msRemaining));

  /** The warning is shown only to a signed-in user who is running out of time. An
   *  active user never sees it: their session is renewed underneath them. */
  const isWarning = computed(() => auth.isAuthenticated && state.value.phase === 'warn');

  const canExtend = computed(() => state.value.canExtend);

  async function extend(): Promise<boolean> {
    if (isExtending.value) return false;
    isExtending.value = true;
    try {
      const renewed = await auth.tryRefreshToken({ signOutOnFailure: false });
      if (renewed) {
        // Clicking the button is itself a sign of life; treat it as activity so the
        // next hour passes without a second interruption.
        lastActivityAt.value = Date.now();
        renewBlockedUntil.value = 0;
      } else {
        renewBlockedUntil.value = Date.now() + RENEW_BACKOFF_MS;
      }
      now.value = Date.now();
      return renewed;
    } finally {
      isExtending.value = false;
    }
  }

  function tick(): void {
    // Recomputed from the absolute expiry on every pass. A backgrounded tab or a
    // sleeping laptop throttles this interval, so a counter that decremented itself
    // would drift out of step with the token it claims to describe.
    now.value = Date.now();

    if (!auth.isAuthenticated) return;

    const { phase } = state.value;

    if (phase === 'renew' && now.value >= renewBlockedUntil.value && !isExtending.value) {
      void extend();
      return;
    }

    if (phase === 'expired') {
      // The countdown said the user would be signed out. Doing anything else here
      // would make the warning a lie, and would leave an unattended machine signed
      // in — which is the whole point of having a session timeout.
      auth.signOut();
    }
  }

  function recordActivity(): void {
    const timestamp = Date.now();
    if (timestamp - lastActivityAt.value < ACTIVITY_THROTTLE_MS) return;
    lastActivityAt.value = timestamp;
  }

  function handleVisibilityChange(): void {
    // Coming back to a throttled tab must settle the countdown immediately rather
    // than at the next scheduled tick.
    if (document.visibilityState === 'visible') tick();
  }

  onMounted(() => {
    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, recordActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    intervalId = setInterval(tick, TICK_MS);
    tick();
  });

  onBeforeUnmount(() => {
    for (const eventName of ACTIVITY_EVENTS) {
      window.removeEventListener(eventName, recordActivity);
    }
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    if (intervalId !== undefined) clearInterval(intervalId);
  });

  return { state, countdown, isWarning, canExtend, isExtending, extend };
}
